import { Box, Divider, IconButton, MenuItem, Typography } from '@mui/material'
import { useEffect } from 'react'
import {
  commentColorMap,
  CommentDiscipline,
  CommentWithReplies,
} from '../../models/Comment'
import CommentComponent from './CommentComponent'
import { useDispatch, useSelector } from 'react-redux'
import { activateTool } from '../../actions/permit'
import { toolsGroup1, toolsGroup5 } from '../Permit/Tools'
import { RootState } from '../../store'
import { ConstructionTab } from '../../reducers/construction'
import {
  setCommentsTypeFilter,
  setIsAddCommentMode,
  setIsCommentsMode,
  updateAddCommentInfo,
} from '../../actions/comments'
import CustomDropdown from '../CustomDropdown'
import { Can } from '../../utils/AbilityUtil'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'
import { setSelectedTool } from '../../actions/construction'
import { BuildingEditingTool } from '../../models/Construction'

interface CommentsClusterProps {
  tab: ConstructionTab
  comments: CommentWithReplies[]
  isDisabled?: boolean
  darkMode?: boolean
}

type CommentDisciplineOrAll = CommentDiscipline | 'All'

const CommentsClusterComponent: React.FC<CommentsClusterProps> = ({
  tab,
  comments,
  isDisabled,
  darkMode,
}) => {
  const dispatch = useDispatch<any>()
  const activeTab = useSelector(
    (state: RootState) => state.construction.selectedConstructionTab,
  )
  const activeTool = useSelector((state: RootState) => state.permit.activeTool)
  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )
  const isCommentsMode = useSelector(
    (state: RootState) => state.comments.isCommentsMode,
  )
  const commentsTypeFilter = useSelector(
    (state: RootState) => state.comments.commentsDisciplineFilter,
  )

  const commentDisciplinesList: CommentDisciplineOrAll[] = [
    'All',
    ...Object.keys(CommentDiscipline)
      .filter((key) => isNaN(Number(key)))
      .map((key) => CommentDiscipline[key as keyof typeof CommentDiscipline]),
  ]

  const handleButtonClick = () => {
    dispatch(setIsCommentsMode(true))
    dispatch(setSelectedTool(BuildingEditingTool.Comment))
    dispatch(activateTool(toolsGroup5.tools[1].name))
  }

  const handleCloseClick = () => {
    dispatch(setIsCommentsMode(false))
    dispatch(setSelectedTool(null))
    dispatch(setIsAddCommentMode(false))
    dispatch(
      updateAddCommentInfo({
        commentDiscipline: null,
        position: { x: 0, y: 0 },
      }),
    )
    dispatch(activateTool(toolsGroup1.tools[0].name))
  }

  const handleFilterByCommentDiscipline = (
    commentDiscipline: CommentDisciplineOrAll,
  ) => {
    dispatch(
      setCommentsTypeFilter(
        commentDiscipline === 'All' ? null : commentDiscipline,
      ),
    )
  }

  useEffect(() => {
    if (activeTab === ConstructionTab.Permit) {
      dispatch(setIsCommentsMode(activeTool === toolsGroup5.tools[1].name))
    } else {
      dispatch(setIsCommentsMode(selectedTool === BuildingEditingTool.Comment))
    }
  }, [activeTool, selectedTool, activeTab])

  return (
    <Can I={AttributeActionNames.Read} a={AttributeResourceNames.Comments}>
      <Box sx={{ width: '32px' }}>
        {!isCommentsMode && (
          <IconButton
            disabled={isDisabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              background: darkMode ? '#353535' : '#F2F2F2',
              width: '100%',
              height: '32px',
              transition: 'opacity 0.5s ease',
              opacity: isCommentsMode ? 0 : 1,
              '&:hover': {
                background: darkMode ? '#434343' : '#1705E5',
              },
            }}
            onClick={handleButtonClick}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='21'
              height='21'
              viewBox='0 0 21 21'
              fill='none'
            >
              <circle cx='10.6962' cy='10.3046' r='9.73913' fill='white' />
              <rect
                x='0.957031'
                y='10.0078'
                width='10.0343'
                height='10.0343'
                fill='white'
              />
            </svg>
          </IconButton>
        )}

        <Box
          sx={{
            position: 'fixed',
            top: '71px',
            right: '11px',
            width: isCommentsMode ? '344px' : '0',
            height: 'calc(100% - 71px - 12px)',
            transition: 'width 0.5s ease',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'start',
          }}
        >
          <IconButton
            disableRipple
            onClick={handleCloseClick}
            sx={{ padding: '0 3px 0 0' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='19'
              viewBox='0 0 19 19'
              fill='none'
            >
              <rect
                width='19'
                height='19'
                rx='6'
                fill={darkMode ? '#353535' : '#F2F2F2'}
              />
              <path
                d='M6.32227 12.9326L9.78838 9.4665L6.32227 6.00039'
                stroke={darkMode ? '#818181' : '#1705E5'}
                strokeMiterlimit='10'
              />
              <path
                d='M10.5332 12.9326L13.9993 9.4665L10.5332 6.00039'
                stroke={darkMode ? '#818181' : '#1705E5'}
                strokeMiterlimit='10'
              />
            </svg>
          </IconButton>
          <Box
            sx={{
              width: '322px',
              height: '100%',
              borderRadius: '4px',
              background: darkMode ? '#353535' : '#fff',
              boxShadow: '0px 3px 5px 0px rgba(47, 47, 47, 0.25)',
            }}
          >
            <Box
              sx={{
                padding: '13px 8px 13px 18px',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: '15px',
                }}
              >
                <Typography
                  sx={{
                    color: darkMode ? '#B9B9B9' : '#04002E',
                    fontSize: '14px',
                    lineHeight: 'normal',
                    ml: '6px',
                  }}
                >
                  Comments
                </Typography>
                <Box
                  gap={'10px'}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CustomDropdown
                    title={
                      commentsTypeFilter
                        ? commentsTypeFilter
                        : 'Choose discipline'
                    }
                    color='#fff'
                    buttonStyleProps={{
                      justifyContent: 'center',
                      padding: '5px',
                      width: '118px',
                      background: commentsTypeFilter
                        ? commentColorMap[commentsTypeFilter]
                        : '#04002E',
                      borderRadius: '72px',
                    }}
                    titleStyleProps={{
                      fontSize: '10px',
                    }}
                  >
                    {commentDisciplinesList
                      .filter((item) => item !== commentsTypeFilter)
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          sx={{
                            background:
                              item === 'All'
                                ? '#04002E'
                                : commentColorMap[item],
                            color: '#fff',
                            '&:hover': {
                              color: '#04002E',
                            },
                          }}
                          onClick={() => handleFilterByCommentDiscipline(item)}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                            }}
                          >
                            {item}
                          </Typography>
                        </MenuItem>
                      ))}
                  </CustomDropdown>
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{ backgroundColor: '#DDD' }}
                  />
                  <IconButton sx={{ padding: 0 }}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='19'
                      height='14'
                      viewBox='0 0 19 14'
                      fill='none'
                    >
                      <path
                        d='M1 11.3306H7M1 6.26443H10M15 5.2512V13.3571M15 13.3571L18 10.3174M15 13.3571L12 10.3174M1 1.19824H13'
                        stroke={darkMode ? '#C8DFFF' : '#1705E5'}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </IconButton>
                </Box>
              </Box>
              <Divider
                flexItem
                sx={{ backgroundColor: darkMode ? '#181818' : '#BBBCBC' }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  height: 'calc(100% - 76px)',
                  pr: '7px',
                  mt: '15px',
                }}
              >
                {comments.map((comment, index) => (
                  <CommentComponent
                    tab={tab}
                    key={index}
                    index={index}
                    comment={comment}
                    darkMode={darkMode}
                    showReplyButton={true}
                    showAllReplies={true}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Can>
  )
}

export default CommentsClusterComponent
